/*
 * Map Controller Component
 * 
 * Handles programmatic map control for zoom and center changes
 * Used when clicking vehicle cards to navigate to map location
 * 
 * FEATURES:
 * - Smooth animation to selected vehicle locations
 * - Customizable animation duration and easing
 * - Auto-centering when vehicles are selected from other tabs
 * 
 * TO EDIT MAP CONTROL:
 * - Animation duration: Modify duration in setView options
 * - Animation easing: Update pan.duration for custom timing
 * - Zoom levels: Adjust zoom parameter based on use case
 */

import { useEffect } from 'react';
import { useMap } from 'react-leaflet';
import { APP_CONSTANTS } from '../../utils/config';

/**
 * MapController Component
 * Provides programmatic control over map view changes
 * 
 * @param {Array} center - [lat, lng] coordinates to center map on
 * @param {number} zoom - Zoom level for the map
 * @param {boolean} disabled - Whether to disable auto-centering (for user-moved maps)
 */
const MapController = ({ center, zoom, disabled = false }) => {
  const map = useMap();
  
  useEffect(() => {
    // Don't auto-center if disabled (e.g., user has manually moved the map)
    if (disabled) {
      //
      return;
    }

    if (center && zoom && map) {
      const attemptSetView = (retryCount = 0) => {
        try {
          // Check if there are any modal dialogs open that might interfere
          const hasOpenModals = document.querySelector('.fixed.inset-0, .modal, [role="dialog"]');
          if (hasOpenModals) {
            // If there are modals open, don't update the main map to avoid conflicts
            const isInModal = map.getContainer().closest('.fixed.inset-0, .modal, [role="dialog"]');
            if (!isInModal) {
              return;
            }
          }

          // Check if map container exists and is in DOM
          const container = map.getContainer();
          if (!container || !container.parentNode) {
            if (retryCount < 3) {
              setTimeout(() => attemptSetView(retryCount + 1), 100);
              return;
            }
            return;
          }

          // Check if map is properly initialized
          if (!map._loaded || !map._container) {
            if (retryCount < 3) {
              setTimeout(() => attemptSetView(retryCount + 1), 100);
              return;
            }
            return;
          }

          // Additional safety check for Leaflet positioning properties
          try {
            const panes = map.getPanes();
            if (!panes || !panes.mapPane) {
              return;
            }
          } catch (paneError) {
            return;
          }

          // Force invalidate size before setting view to ensure proper DOM positioning
          try {
            map.invalidateSize();
          } catch (invalidateError) {
          }
          
          // Add a small delay to allow DOM elements to be properly positioned
          setTimeout(() => {
            try {
              // Check one more time if positioning elements exist
              const mapContainer = map.getContainer();
              if (!mapContainer || !mapContainer._leaflet_pos) {
                // Try to initialize positioning if missing
                if (mapContainer) {
                  mapContainer._leaflet_pos = { x: 0, y: 0 };
                }
              }

              // Smooth animation to the selected location
              map.setView(center, zoom, {
                animate: true,
                pan: {
                  duration: APP_CONSTANTS.UI.ANIMATION_DURATION / 1000, // Convert to seconds
                },
                zoom: {
                  animate: true
                }
              });
            } catch (animationError) {
              // Fallback: try without animation
              try {
                map.setView(center, zoom, { animate: false });
              } catch (noAnimError) {
              }
            }
          }, 50);
          
        } catch (error) {
          
          // Fallback: try without animation
          try {
            map.invalidateSize();
            map.setView(center, zoom, { animate: false });
          } catch (fallbackError) {
            
            // Final retry without animation after a delay
            if (retryCount < 2) {
              setTimeout(() => attemptSetView(retryCount + 1), 200);
            }
          }
        }
      };
      
      // Start the attempt
      attemptSetView();
    }
  }, [center, zoom, map, disabled]);

  // This component doesn't render anything
  return null;
};

export default MapController;